import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) return <NotFound />;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.metaDescription,
    "image": article.image,
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Finds Polska",
      "url": import.meta.env.VITE_SITE_URL || "https://kakobuyfinds.fun",
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Finds Polska",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png",
      },
    },
    "datePublished": article.date,
    "dateModified": article.date,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://kakobuyfinds.fun"}/artykul/${article.slug}`,
    },
    "inLanguage": "pl-PL",
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <Navbar />
      <main>
        <article>
          <header className="relative w-full pt-32 pb-16 px-4 md:px-6 bg-background-100">
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background-100/70 via-background-100/60 to-background-100"></div>
            </div>
            <div className="relative z-10 max-w-3xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-300/40 font-label text-xs font-medium text-foreground-600 mb-6">
                Kakobuy Spreadsheet Poradnik
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-6">
                {article.title}
              </h1>
              <p className="font-body text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                {article.summary}
              </p>
              <div className="flex items-center justify-center gap-3 mt-6">
                <div className="w-10 h-10 rounded-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20friendly%20young%20European%20person%20with%20glasses%20confident%20smile%20clean%20white%20background%20soft%20natural%20lighting%20approachable%20modern%20style%20editorial%20headshot&width=100&height=100&seq=article-avatar-gen&orientation=squarish"
                    alt={article.authorName}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="text-left">
                  <p className="font-label text-sm font-semibold text-foreground-950">{article.authorName}</p>
                  <p className="font-body text-xs text-foreground-500">Aktualizacja: {new Date(article.date).toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })}</p>
                </div>
              </div>
            </div>
          </header>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-12">
            <div className="prose-custom">
              {article.sections.map((section, si) => (
                <section key={si} className="mb-10">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                    {si + 1}. {section.title}
                  </h2>
                  <div className="space-y-4 font-body text-base text-foreground-700 leading-relaxed">
                    {section.paragraphs.map((p, pi) => (
                      <p key={pi}>{p}</p>
                    ))}
                  </div>
                </section>
              ))}

              <section className="mb-8">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  Podsumowanie
                </h2>
                <div className="bg-accent-50/50 rounded-2xl p-6">
                  <p className="font-body text-base text-foreground-700 leading-relaxed">
                    <strong className="text-foreground-950">Kakobuy Spreadsheet</strong> to niezastąpione narzędzie dla każdego użytkownika Kakobuy. Niezależnie od tego, czy szukasz butów, ubrań czy akcesoriów — nasz arkusz pomoże Ci znaleźć najlepsze produkty w najlepszych cenach. Regularnie sprawdzaj aktualizacje i dołącz do rosnącej społeczności <strong className="text-foreground-950">Kakobuy Finds Polska</strong>.
                  </p>
                </div>
              </section>
            </div>

            <nav className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10 pt-8 border-t border-background-200/60">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 font-label text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Wróć do Strony Głównej
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 font-label text-sm font-semibold rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Kakobuy Spreadsheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </nav>

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6 text-center">
                Inne Poradniki Kakobuy Spreadsheet
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 6)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/artykul/${a.slug}`}
                      className="group p-4 rounded-xl bg-background-100 hover:bg-accent-50/30 transition-colors cursor-pointer"
                    >
                      <div className="w-full h-32 rounded-lg overflow-hidden mb-3">
                        <img
                          src={a.image}
                          alt={a.title}
                          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h4 className="font-label text-sm font-semibold text-foreground-950 group-hover:text-accent-600 transition-colors line-clamp-2">
                        {a.title}
                      </h4>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}